/**
 * Reducer to manage recipient related state
 */

import { ActionTypes as types } from '../constants'
import serverProps from '../utils/serverProps'
import { MONEY_PAGE } from '../../../../lib/constants'
import {
  isPhoneNumberMaybe,
  parseAndFormatPhoneNumber,
} from 'transfer/utils/phoneUtils'
import { getDisplayNameFromContact } from '../../lib/contact-utils'

function getInitialState(props) {
  const {
    recipientData: {
      payerId = '',
      recipientCountryCode: country = '',
      recipient = '',
      recipientId = '',
      recipientIdType = '',
      displayName: recipientName = '',
      isCharity = false,
      firstName = '',
      lastName = '',
      thumbnailUrl = '',
      lastTransactionType = '',
      recipientDisplayDetails = '',
      // whether recipient accepts multiple payment types (GS/FF)
      paymentTypes = [],
      suggestedPaymentType,
      isHardDefault = false,
      isServiceSupportedExternalFlow = false,
      cipFlow,
      paymentTypeSelectionMessage = '',
      paymentTypeTogglePersonalMessage = '',
      paymentTypeTogglePurchaseMessage = '',
      moneyPageOverPanel = MONEY_PAGE.CONTROL_WEB,
      suggestedDesign = MONEY_PAGE.CONTROL_WEB,
      serviceContext = {},
      infoNeededFromCustomer = [],
      moreInfoIsNeededForAssessCapabilities = false,
      recipientAccountType = '',
      recurringType = '',
      hasNetworkId = false,
      networkId = '',
      productId = '',
      productQuantity = '',
      accountId = '',
      networkIdDisplayName = '',
      networkDisplayName = '',
      mayCashAdvanceFeesApply = false,
      token = '',
      isRecipientCurrencyRestricted = false,
      pispRecipientType = '',
      pispRecipientId = '',
      isNoteRestricted = false,
      purchaseConsentRequired = false,
      purchaseConsentLabel = '',
      purchaseConsentChecked = false,
      isAShop = false,
      typedRecipient = '',
    } = {},
    payRequest: {
      displayName: payRequestDisplayName = '',
      thumbnailUrl: payRequestThumbnailUrl = '',
      firstName: payRequestFirstName = '',
      lastName: payRequestLastName = '',
      recipient: payRequestRecipient = '',
      noteFromRecipient = '',
    } = {},
    recipientType = '',
    profile = {},
  } = props

  // TODO profile and reducer are being used when guest is ppme, and it absolutely needs to not be...
  // Consolidate all guest payer/payee stuff somewhere else (for guest pay a request, and ppme)
  const {
    recipient: profileRecipient = '',
    theme = '',
    displayName: profileName = '',
    thumbnailUrl: thumbnail = '',
    ppmeBackgroundColor = '',
    campaignName: campaignName = '',
    ppmeProfilePhoto: { photo_url: ppmePhotoUrl = '' } = {},
    isBusiness,
    countryCode: ppmeCountryCode,
  } = profile

  return {
    accountId,
    campaignName,
    cipFlow,
    contact: {},
    country,
    displayName: recipientName || profileName || payRequestDisplayName,
    firstInitial: firstName.charAt(0) || payRequestFirstName.charAt(0),
    firstName: firstName || payRequestFirstName,
    hasNetworkId,
    infoNeededFromCustomer,
    isAShop,
    isBusiness,
    isCharity,
    isGSOnlyRecipient: false,
    isHardDefault,
    isNoteRestricted,
    isPaypalAccount: false,
    isRecipientCurrencyRestricted,
    isRecipientValid: !!(recipientId || recipient), // TODO: check for validity rather than existence?
    isServiceSupportedExternalFlow,
    lastInitial: lastName.charAt(0) || payRequestLastName.charAt(0),
    lastName: lastName || payRequestLastName,
    lastTransactionType,
    mayCashAdvanceFeesApply,
    moneyPageOverPanel,
    moreInfoIsNeededForAssessCapabilities,
    networkDisplayName,
    networkId,
    networkIdDisplayName,
    noteFromRecipient,
    payerId,
    paymentTypeSelectionMessage,
    paymentTypeTogglePersonalMessage,
    paymentTypeTogglePurchaseMessage,
    paymentTypes,
    pispRecipientId,
    pispRecipientType,
    ppmeBackgroundColor,
    ppmeCountryCode,
    ppmePhotoUrl,
    productId,
    productQuantity,
    purchaseConsentChecked,
    purchaseConsentLabel,
    purchaseConsentRequired,
    recipient: recipient || profileRecipient || payRequestRecipient,
    recipientAccountType,
    recipientDisplayDetails,
    recipientId, // ppme alternative to recipient
    recipientIdType, // either '' or 'ppme' <-- WUT?
    recipientType,
    recurringType,
    serviceContext,
    suggestedDesign,
    suggestedPaymentType,
    theme,
    thumbnailUrl: thumbnailUrl || thumbnail || payRequestThumbnailUrl,
    token,
    typedRecipient,
  }
}

const initialState = getInitialState(serverProps)

// prettier-ignore
export default (state = initialState, action) => { // NOSONAR
  switch (action.type) {
    case types.EXTERNAL.RECEIVED_DATA: {
      // is this even a good idea?
      return getInitialState({ ...state, ...action.data })
    }

    case types.EXTERNAL.RECEIVED_EXTERNAL_PARAMS: {
      let {
        recipient,
        recipientDisplayDetails = '',
        displayName,
        firstName,
        lastName,
        profile_image,
        thumbnailUrl,
        transaction_type,
      } = action.data
      const isRecipientValid = !!recipient

      if (profile_image && profile_image.image_url) {
        // choose the better thumbnail if possible
        thumbnailUrl = profile_image.image_url
      }

      const firstInitial = firstName ? firstName.charAt(0) : ''
      const lastInitial = lastName ? lastName.charAt(0) : ''

      return {
        ...state,
        ...(recipient
          ? { recipient, isRecipientValid, recipientDisplayDetails }
          : null),
        ...(displayName && { displayName }),
        firstName,
        lastName,
        ...(thumbnailUrl && { thumbnailUrl }),
        firstInitial,
        lastInitial,
        transaction_type,
      }
    }
    case types.RECIPIENT.CHANGE_RECIPIENT:
      return {
        ...initialState, // reset everything
        recipient: action.data.recipient,
        isRecipientValid: action.data.isRecipientValid,
        isCharity: !!action.data.isCharity,
        typedRecipient: state.typedRecipient || '',
        ...(action.data.token && { token: action.data.token }), // If provided, keep existing token i.e. for send international
      }

    case types.RECIPIENT.SELECT_UNILATERAL_CONTACT:
    case types.RECIPIENT.SELECT_CONTACT: {
      let {
        payerId = '',
        fullName = '',
        firstName = '',
        lastName = '',
        alternateFullName = '',
        companyName,
        displayName = '',
        profile_image,
        thumbnailUrl = '',
        email,
        phones = [],
        isGSOnlyRecipient = false,
        hasNetworkId = false,
        networkIdDisplayName = '',
        networkId = '',
        networkDisplayName = '',
        moneyPageOverPanel = '',
        infoNeededFromCustomer = [],
        moreInfoIsNeededForAssessCapabilities = false,
        mayCashAdvanceFeesApply = false,
        recipientAccountType = '',
        recurringType = '',
        isUnilateral,
        isRestricted,
        token = '',
        typedRecipient = '',
      } = action.data.contact
      const { user: { country: senderCountryCode } = {} } = state

      let firstInitial = ''
      let lastInitial = ''

      if (profile_image && profile_image.image_url) {
        // choose the better thumbnail if possible
        thumbnailUrl = profile_image.image_url
      }

      if (firstName && lastName) {
        // build out the initials if fullName exists
        firstInitial =
          firstName.charAt(0) ||
          alternateFullName.charAt(0) ||
          companyName.charAt(0)
        lastInitial = lastName.charAt(0)
      }

      // todo break all of this logic out into a util (that uses griffin?) *search this comment to see everywhere*
      // pull out the first phone numebr
      let [phone = {}] = phones

      // pull out the phone_number property, which will be either object or string
      const { phone_number = {} } = phone

      // try to pull out the properties
      const { country_code = '', national_number = '' } = phone_number

      // if the properties exist, let's construct the string, else phone_number
      // is a string so just use that
      if (country_code && national_number) {
        phone = `+${country_code} ${national_number}`
      } else {
        phone = ''
      }

      let recipientDisplayDetails
      if (email) {
        recipientDisplayDetails = email

        // non-contact recipient form input is always passed as email
        if (isPhoneNumberMaybe(email)) {
          const formattedPhoneNumber = parseAndFormatPhoneNumber(
            email,
            senderCountryCode
          )
          if (formattedPhoneNumber) {
            recipientDisplayDetails = formattedPhoneNumber
          }
        }
      } else {
        recipientDisplayDetails = phone

        const formattedPhoneNumber = parseAndFormatPhoneNumber(
          phone,
          senderCountryCode
        )
        if (formattedPhoneNumber) {
          recipientDisplayDetails = formattedPhoneNumber
        }
      }

      const networkIdInfo = {
        recipientDisplayDetails,
      }
      if (hasNetworkId) {
        networkIdInfo.recipientDisplayDetails = networkIdDisplayName
      }
      networkIdInfo.networkId = networkId
      networkIdInfo.networkDisplayName = networkDisplayName

      displayName = getDisplayNameFromContact(action.data.contact)

      typedRecipient = action.data.contact.typedRecipient || ''

      return {
        ...state,
        contact: action.data.contact,
        payerId,
        recipient: email || phone,
        isRecipientValid: true,
        displayName,
        firstName,
        lastName,
        fullName,
        alternateFullName,
        firstInitial,
        lastInitial,
        thumbnailUrl,
        isGSOnlyRecipient,
        moneyPageOverPanel,
        infoNeededFromCustomer,
        moreInfoIsNeededForAssessCapabilities,
        mayCashAdvanceFeesApply,
        recipientAccountType,
        recurringType,
        isUnilateral,
        isRestricted,
        token,
        ...networkIdInfo,
        typedRecipient,
      }
    }

    case types.RECIPIENT.UPDATE_RECEIVER_COUNTRY:
      return {
        ...state,
        country: action.data.countryCode,
      }

    case types.RECIPIENT.CHANGE_TYPED_RECIPIENT:
      return {
        ...state,
        typedRecipient: action.data.recipient,
      }

    case types.TRANSACTION.RECEIVED_FUNDING_OPTIONS:
      return {
        ...state,
        isPaypalAccount: action.data.isPaypalAccount,
      }

    case types.TRANSACTION.CHANGE_IS_HARD_DEFAULT:
      return {
        ...state,
        isHardDefault: action.data.isHardDefault,
      }

    case types.TRANSACTION.UPDATE_TOKEN:
      return {
        ...state,
        token: action.data.token,
        moreInfoIsNeededForAssessCapabilities:
          action.data.moreInfoIsNeededForAssessCapabilities,
      }

    case types.TRANSACTION.UPDATE_MONEY_PAGE:
      return {
        ...state,
        moneyPageOverPanel: action.data.moneyPageOverPanel,
      }

    case types.TRANSACTION.UPDATE_SUGGESTED_DESIGN:
      return {
        ...state,
        suggestedDesign: action.data.suggestedDesign,
      }

    case types.TRANSACTION.CHANGE_HIGHLIGHTED_PAYMENT_TYPE:
      return {
        ...state,
        highlightPaymentType: action.data.highlightPaymentType,
      }

    case types.TRANSACTION.UPDATE_PAYMENT_TYPE_SELECTION_MESSAGE:
      return {
        ...state,
        paymentTypeSelectionMessage: action.data.message,
      }

    case types.TRANSACTION.UPDATE_PAYMENT_TYPE_TOGGLE_PERSONAL_MESSAGE:
      return {
        ...state,
        paymentTypeTogglePersonalMessage: action.data.message,
      }

    case types.TRANSACTION.UPDATE_PAYMENT_TYPE_TOGGLE_PURCHASE_MESSAGE:
      return {
        ...state,
        paymentTypeTogglePurchaseMessage: action.data.message,
      }

    case types.TRANSACTION.RECIPIENT_CURRENCY_RESTRICTED:
      return {
        ...state,
        isRecipientCurrencyRestricted:
          action.data.isRecipientCurrencyRestricted,
      }

    case types.TRANSACTION.CHANGE_RECOMMENDED_PAYMENT_TYPE:
      return {
        ...state,
        recommendedPaymentType: action.data.recommendedPaymentType,
      }

    case types.TRANSACTION.CANCELLED_TRANSACTION:
    case types.TRANSACTION.LEAVING_SUCCESS_PAGE:
      return {
        ...initialState,
        paymentTypeSelectionMessage: '',
        paymentTypeTogglePersonalMessage: '',
        paymentTypeTogglePurchaseMessage: '',
        isAShop: false,
        typedRecipient: '',
      }

    case types.TRANSACTION.SUBMITTING_RECIPIENT:
      return {
        ...state,
        selectedType: action.data.selectedType,
      }
    case types.TRANSACTION.NOTE_FIELD_IS_RESTRICTED:
      return {
        ...state,
        isNoteRestricted: action.data.isNoteRestricted,
      }
    case types.RECIPIENT.CHANGE_DISPLAY_NAME:
      return {
        ...state,
        contact: {
          ...state.contact,
          displayName: action.data.displayName,
        },
      }
    case types.RECIPIENT.UPDATE_RECEIVER_PHOTO:
      return {
        ...state,
        thumbnailUrl: action.data.receiver.profilePhoto,
      }
    default:
      return state
  }
}
